import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { useCampaignActionsModals } from '../../Modals/CampaignActionsModalsProvider'

import { useToast } from '@percent/lemonade'
import {
  GivingCampaignStatusDto,
  type GivingCampaignPartnerDto
} from '@percent/workplace-giving/api/campaigns/campaign.types'
import { permission } from '@percent/workplace-giving/common/hoc/withPermissions'
import { Permissions } from '@percent/workplace-giving/constants/permissions'
import { usePermission } from '@percent/workplace-giving/common/hooks/usePermission/usePermission'

export type UseGivingCampaignActionsProps = Readonly<{ campaign: GivingCampaignPartnerDto }>

export type UseGivingCampaignActionsReturn = Readonly<{
  view: () => void
  edit: () => void
  publish: () => void
  unpublish: () => void
  cancel: () => void
}>

export const useGivingCampaignActions = ({
  campaign
}: UseGivingCampaignActionsProps): UseGivingCampaignActionsReturn => {
  const has = usePermission()
  const navigate = useNavigate()
  const { addToast } = useToast()
  const { t } = useTranslation()

  const canManageGivingCampaigns = has(permission(Permissions.CampaignsWrite))

  const { publishCampaign, unpublishCampaign, cancelCampaign } = useCampaignActionsModals()

  const view = () => {
    navigate(`/campaigns/${campaign.id}`)
  }

  const edit = useCallback(() => {
    if (!canManageGivingCampaigns) {
      addToast(t('workplace_giving.insufficientPermissions.text'), 'error')

      return
    }

    if (campaign.status === GivingCampaignStatusDto.CANCELLED) {
      addToast(t('workplace_giving.myCampaigns.actions.apiErrors.campaignCancelled'), 'error')

      return
    }

    navigate(`/campaigns/${campaign.id}/edit`)
  }, [canManageGivingCampaigns, campaign.status, campaign.id, t, addToast, navigate])

  const publish = useCallback(() => {
    if (!canManageGivingCampaigns) {
      addToast(t('workplace_giving.insufficientPermissions.text'), 'error')

      return
    }

    if (campaign.status === GivingCampaignStatusDto.PUBLISHED) {
      return
    }

    if (campaign.status === GivingCampaignStatusDto.CANCELLED) {
      addToast(t('workplace_giving.myCampaigns.actions.apiErrors.campaignCancelled'), 'error')

      return
    }

    publishCampaign({ id: campaign.id, title: campaign.title })
  }, [canManageGivingCampaigns, campaign.status, campaign.id, campaign.title, t, addToast, publishCampaign])

  const unpublish = useCallback(() => {
    if (!canManageGivingCampaigns) {
      addToast(t('workplace_giving.insufficientPermissions.text'), 'error')

      return
    }

    if (campaign.status === GivingCampaignStatusDto.DRAFT) {
      return
    }

    if (campaign.status === GivingCampaignStatusDto.CANCELLED) {
      addToast(t('workplace_giving.myCampaigns.actions.apiErrors.campaignCancelled'), 'error')

      return
    }

    unpublishCampaign({ id: campaign.id, title: campaign.title })
  }, [canManageGivingCampaigns, campaign.status, campaign.id, campaign.title, t, addToast, unpublishCampaign])

  const cancel = useCallback(() => {
    if (!canManageGivingCampaigns) {
      addToast(t('workplace_giving.insufficientPermissions.text'), 'error')

      return
    }

    if (campaign.status === GivingCampaignStatusDto.CANCELLED) {
      return
    }

    cancelCampaign({ id: campaign.id, title: campaign.title })
  }, [canManageGivingCampaigns, campaign.status, campaign.id, campaign.title, t, addToast, cancelCampaign])

  return { view, edit, publish, unpublish, cancel }
}
